/**
 * Pool — lista giocatori in rosa disponibili (non in titolari/panca).
 *
 * Ogni card e' draggable verso pitch o bench. Andrea 2026-09-09: il drag
 * su mobile e' scomodo, quindi ogni card e' anche cliccabile —
 *   - click singolo  -> popup "dove lo schiero?" (ruoli del modulo)
 *   - doppio click   -> schierato d'ufficio nel suo ruolo primario
 * gestiti dalla page (FormazionePage) tramite le callback qui sotto.
 */

import { useEffect, useRef, useState } from "react";
import type { RosaPlayer } from "../../hooks/useFase2";
import { PlayerAvatar } from "../common/PlayerAvatar";
import { ruoloMantraClass } from "../common/ruoliMantra";
import { siglaRuolo, sigleRuoli } from "../common/nomenclatura";
import { badgeProbabilita, testoInfoProbabilita } from "../common/probabilita";

function classicLetter(r: string | null): string {
  if (!r) return "?";
  const c = r.charAt(0).toUpperCase();
  return ["P", "D", "C", "A"].includes(c) ? c : "?";
}

/** Finestra entro cui due click sullo stesso giocatore valgono "doppio". */
const DOPPIO_CLICK_MS = 260;

interface PoolProps {
  giocatori: RosaPlayer[];
  usedIds: Set<string>;
  /** Click singolo su un giocatore disponibile (apre il popup ruoli). */
  onPlayerClick?: (p: RosaPlayer) => void;
  /** Doppio click: schieramento diretto nel ruolo primario. */
  onPlayerDoubleClick?: (p: RosaPlayer) => void;
  /** Andrea 2026-10-23: % / 🟥 / ✚ anche in tribuna, senza aprire niente. */
  probabilitaPerGiocatore?: Map<
    string,
    { probabilita: number; fonte: string; note: string | null }
  >;
}

export function Pool({
  giocatori,
  usedIds,
  onPlayerClick,
  onPlayerDoubleClick,
  probabilitaPerGiocatore,
}: PoolProps) {
  // fumetto col motivo per squalificati/infortunati (un tocco sul badge)
  const [infoId, setInfoId] = useState<string | null>(null);
  useEffect(() => {
    if (!infoId) return;
    const id = setTimeout(() => setInfoId(null), 4000);
    return () => clearTimeout(id);
  }, [infoId]);
  // Doppio click fatto a mano invece che con onDoubleClick: il primo click
  // apre un overlay a tutto schermo, quindi il secondo tocco NON finisce
  // piu' sulla card e il dblclick nativo non scatta (ne' su desktop ne' su
  // mobile). Ritardiamo quindi il singolo click e lo annulliamo se ne
  // arriva un secondo sullo stesso giocatore.
  const timer = useRef<number | null>(null);
  const ultimoId = useRef<string | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  function handleClick(p: RosaPlayer) {
    if (timer.current !== null && ultimoId.current === p.giocatore_id) {
      window.clearTimeout(timer.current);
      timer.current = null;
      ultimoId.current = null;
      onPlayerDoubleClick?.(p);
      return;
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    ultimoId.current = p.giocatore_id;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      ultimoId.current = null;
      onPlayerClick?.(p);
    }, DOPPIO_CLICK_MS);
  }

  // Andrea 2026-05-18: i giocatori provvisori (fuori lista 25 ufficiale)
  // NON sono schierabili. Mostriamo solo gli ufficiali (convocati) non
  // ancora messi in formazione.
  const disponibili = giocatori.filter(
    (p) => p.tipo_iscrizione === "ufficiale" && !usedIds.has(p.giocatore_id),
  );

  // Andrea 2026-10-23: una lista sola come la panchina (righe uniformi,
  // niente titoli per reparto), sempre in ordine portieri, difensori,
  // centrocampisti, attaccanti; a parita' di reparto per nome.
  const ORDINE: Record<string, number> = { P: 0, D: 1, C: 2, A: 3, "?": 4 };
  const ordinati = [...disponibili].sort((a, b) => {
    const d = ORDINE[classicLetter(a.ruolo_classic)] - ORDINE[classicLetter(b.ruolo_classic)];
    return d !== 0 ? d : a.nome.localeCompare(b.nome);
  });

  // Andrea 2026-09-07: il totale sono i CONVOCATI (lista 25 ufficiale),
  // non l'intera rosa (fino a 40): i provvisori non sono schierabili.
  const convocati = giocatori.filter((p) => p.tipo_iscrizione === "ufficiale");

  return (
    <div className="card p-2 space-y-2">
      <div className="text-xs font-bold text-fg-muted uppercase tracking-wider px-1">
        Rosa disponibile ({disponibili.length}/{convocati.length} convocati)
      </div>
      {onPlayerClick && (
        <div className="text-[10px] text-fg-muted px-1 -mt-1">
          Click = scegli il ruolo · doppio click = ruolo primario · oppure trascina sul campo
        </div>
      )}
      <div className="flex flex-col gap-1">
        {ordinati.map((p) => {
          const pr = probabilitaPerGiocatore?.get(p.giocatore_id);
          const b = pr ? badgeProbabilita(pr.probabilita, pr.note) : null;
          const info = pr ? testoInfoProbabilita(pr.probabilita, pr.note, pr.fonte) : "";
          return (
            <div
              key={p.giocatore_id}
              draggable
              role="button"
              tabIndex={0}
              onClick={() => handleClick(p)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onPlayerClick?.(p);
                }
              }}
              onDragStart={(e) => {
                e.dataTransfer.setData(
                  "application/x-fantasquad",
                  JSON.stringify({
                    giocatore_id: p.giocatore_id,
                    sorgente: "pool",
                  }),
                );
                e.dataTransfer.effectAllowed = "copy";
              }}
              className="relative w-full rounded-lg border-2 border-primary-soft bg-bg-card hover:border-accent flex items-center gap-2 px-2 py-1 cursor-pointer active:cursor-grabbing select-none touch-manipulation text-xs transition"
              title={`${p.nome}${
                p.ruoli_mantra ? " · " + sigleRuoli(p.ruoli_mantra) : ""
              } — click per scegliere il ruolo, doppio click per schierarlo nel ruolo primario`}
            >
              <PlayerAvatar nome={p.nome} size={28} className="shrink-0" />
              {p.ruoli_mantra && p.ruoli_mantra.length > 0 && (
                <div className="flex gap-0.5 shrink-0">
                  {p.ruoli_mantra.map((rm) => (
                    <span
                      key={rm}
                      className={`text-[10px] font-bold min-w-[24px] h-6 px-1 rounded flex items-center justify-center font-mono ${ruoloMantraClass(
                        rm,
                      )}`}
                    >
                      {siglaRuolo(rm)}
                    </span>
                  ))}
                </div>
              )}
              <span className="font-bold truncate flex-1 min-w-0">{p.nome}</span>
              {b && (
                <span className="relative shrink-0">
                  <span
                    className={`text-[9px] font-bold font-mono px-1 py-0.5 rounded ${b.cls}`}
                    title={info}
                    onClick={
                      b.icona
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setInfoId((cur) => (cur === p.giocatore_id ? null : p.giocatore_id));
                          }
                        : undefined
                    }
                  >
                    {b.label}
                  </span>
                  {infoId === p.giocatore_id && (
                    <span
                      className="absolute bottom-full right-0 mb-1 z-40 w-48 rounded-lg bg-primary-deep border border-accent/50 shadow-xl px-2 py-1.5 text-[10px] leading-snug text-fg whitespace-normal font-normal"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInfoId(null);
                      }}
                    >
                      {info}
                    </span>
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
